<template>
  <div>
    <Navbar />

    <main class="auth-page">
      <div class="auth-bg">
        <div class="auth-bg-overlay"></div>
      </div>

      <div class="auth-container">
        <div class="auth-card">

          <!-- ── Success state ── -->
          <template v-if="sent">
            <div class="verify-state">
              <div class="result-icon-wrap result-icon-wrap--success">
                <font-awesome-icon icon="envelope-open-text" class="result-icon" />
              </div>
              <h3 class="auth-title">Check your inbox</h3>
              <p class="auth-sub">
                We've sent a verification link to <strong>{{ sentEmail }}</strong>.
                Click the link in the email to activate your account.
              </p>
              <p class="resend-hint">
                Didn't receive it?
                <button class="resend-btn" @click="resend" :disabled="loading">
                  <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                  Resend email
                </button>
              </p>
              <router-link to="/auth/login" class="submit-btn" style="text-decoration:none; justify-content:center;">
                <font-awesome-icon icon="arrow-right" class="me-1" /> Go to Login
              </router-link>
            </div>
          </template>

          <!-- ── Form state ── -->
          <template v-else>
            <div class="auth-card-header">
              <div class="envelope-icon-wrap">
                <font-awesome-icon icon="envelope" class="envelope-icon" />
              </div>
              <h3 class="auth-title">Resend Verification</h3>
              <p class="auth-sub">Enter your email and we'll send you a new verification link.</p>
            </div>

            <!-- Validation errors -->
            <div v-if="hasErrors" class="error-box">
              <div class="error-box-title">
                <font-awesome-icon icon="exclamation-circle" class="me-1" /> Error:
              </div>
              <ul class="error-list">
                <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <form @submit.prevent="resendVerificationMail" novalidate>
              <div class="form-group-styled">
                <label class="form-label-styled">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input-styled"
                  :class="{ 'input--error': hasErrors }"
                  placeholder="you@company.com"
                  autocomplete="email"
                  required
                />
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="loading || !email"
              >
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Sending link...' : 'Send Verification Link' }}
              </button>
            </form>

            <div class="auth-card-footer">
              <router-link to="/auth/login" class="back-link">
                <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Login
              </router-link>
            </div>
          </template>

        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

export default {
  name: 'ResendVerification',

  components: { Navbar, Footer },

  setup() {
    const toast = useToast();

    const email            = ref('');
    const loading          = ref(false);
    const sent             = ref(false);
    const sentEmail        = ref('');
    const validationErrors = ref({});

    const hasErrors  = computed(() => Object.keys(validationErrors.value).length > 0);
    const flatErrors = computed(() => Object.values(validationErrors.value).flat());

    const normaliseErrors = (msg) => {
      if (!msg) return {};
      if (typeof msg === 'string')  return { general: [msg] };
      if (Array.isArray(msg))       return { general: msg.flat() };
      return msg;
    };

    const doSend = async () => {
      if (!email.value) return;
      loading.value = true;
      validationErrors.value = {};

      try {
        await axios.post('/send-verification-email', { email: email.value, type: 'web' });
        sentEmail.value = email.value;
        email.value     = '';
        sent.value      = true;
        toast.success('Verification link sent — please check your inbox.');
      } catch (err) {
        const status  = err.response?.status;
        const message = err.response?.data?.message;
        if (status === 422) {
          validationErrors.value = normaliseErrors(message);
          toast.error('There is an error in your input.');
        } else {
          validationErrors.value = normaliseErrors(message ?? 'An error occurred. Please try again.');
          toast.error(message ?? 'An error occurred. Please try again.');
        }
      } finally {
        loading.value = false;
      }
    };

    const resendVerificationMail = doSend;

    const resend = () => {
      email.value = sentEmail.value;
      sent.value  = false;
      doSend();
    };

    return {
      email, loading, sent, sentEmail,
      validationErrors, hasErrors, flatErrors,
      resendVerificationMail, resend,
    };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.auth-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.auth-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.96) 0%, rgba(0,74,173,.82) 100%);
}

.auth-container {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 24px;
  min-height: 100vh;
}

/* ── Card ────────────────────────────────────────────────────────────── */
.auth-card {
  background: #fff;
  border-radius: 16px;
  padding: 44px 40px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

/* ── Header ──────────────────────────────────────────────────────────── */
.auth-card-header   { margin-bottom:26px; text-align:center; }
.envelope-icon-wrap { width:60px; height:60px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; margin:0 auto 18px; }
.envelope-icon      { font-size:22px; }
.auth-title         { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 8px; }
.auth-sub           { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.55; }
.me-1               { margin-right:4px; }

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box       { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 8px 8px 0; padding:12px 14px; margin-bottom:20px; }
.error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:6px; display:flex; align-items:center; }
.error-list      { margin:0; padding-left:16px; }
.error-list li   { font-size:12.5px; color:#c02828; margin-bottom:3px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.form-group-styled { display:flex; flex-direction:column; gap:6px; margin-bottom:18px; }
.form-label-styled { font-size:13px; font-weight:600; color:#1a2b45; }

.form-input-styled {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #dde5f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1a2b45;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
}
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.09); }
.input--error { border-color:#e03535 !important; }

/* ── Submit ──────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 13px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.submit-btn:hover:not(:disabled) { background:#003b8e; }
.submit-btn:active:not(:disabled) { transform:scale(.98); }
.submit-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.auth-card-footer { margin-top:20px; padding-top:16px; border-top:1px solid #f0f4f8; display:flex; justify-content:center; }
.back-link { display:inline-flex; align-items:center; gap:5px; color:#7a90b0; font-weight:600; font-size:13px; text-decoration:none; transition:color .15s; }
.back-link:hover { color:#004AAD; }

/* ── Success state ───────────────────────────────────────────────────── */
.verify-state       { display:flex; flex-direction:column; align-items:center; text-align:center; gap:16px; }
.result-icon-wrap   { width:72px; height:72px; border-radius:50%; display:flex; align-items:center; justify-content:center; }
.result-icon-wrap--success { background:rgba(0,74,173,.1); color:#004AAD; }
.result-icon        { font-size:28px; }
.resend-hint        { font-size:13px; color:#7a90b0; margin:0; }
.resend-btn         { background:transparent; border:none; color:#004AAD; font-size:13px; font-weight:700; cursor:pointer; padding:0; text-decoration:underline; }
.resend-btn:hover   { color:#003b8e; }
.resend-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .auth-card      { padding:32px 20px; }
  .auth-container { padding:24px 16px; }
}
</style>